import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {connect, ConnectedProps, useDispatch} from "react-redux";
import {AlertTypes, apiClient, Grid, setAlert, Paper, Typography, Skeleton, useTheme, MuiIcons} from "@leap4/frontend";
import {setIsLoading} from "../../../../store/actions/dateRange.actions";
import {RootState} from "../../../../interfaces/root-state.interface";
import {IDateRangeGState, ILoadingComponents} from "../../../../interfaces/datesGlobalState.interface";
import {AdServerTagsBaseUrl, ComparisonState} from "../../../../config/generals";
import ReportHeader from "../../common/ReportHeader/ReportHeader";
import {urlService} from "../../../routing/urlsService";
import {TagInfo, TagMetrics} from "./components/tagGeneralInfo";
import ConnectedTagsTable, {connectedAdServerTag} from "./components/connectedTagsTable";
import {getDefaultTimeDisplay, indexToTimeInterval} from "../../common/charts/charts";
import ComparisonOverviewGraph, { IOverviewGraphResponse } from "../../common/ComparisonGraph";
import {MetricsComparison} from "../../../shared/MetricsComparison/MetricsComparison.container";
import {IAdServerTagOverview} from "./interfaces";
import {metricsComparisonKeyMap} from "../../../shared/MetricsComparison/MetricsComparisonHelpers";
import {useRefreshInterval} from "../../../../store/hooks";
import moment from "moment";
import {checkIfDateIsInComparisonOptions, getComparisonOptionsDates} from "../../common/time.utils";

const { Tag, Toc: TocIcon, Person4: Person4Icon, GpsFixed: GpsFixedIcon } = MuiIcons;
const mapStateToProps = (state: RootState) => ({
    dates: state.dates,
});

const mapActions = {
    setIsLoading
}

const connector = connect(mapStateToProps, mapActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

export interface IProps extends PropsFromRedux {
    dates: IDateRangeGState
    setIsLoading: (isLoading: Partial<ILoadingComponents>) => void
}


const AdServerTagPage = ({dates: {startDate, endDate, isLoading, isLoadingAny,selectedComparison}, setIsLoading}:IProps): JSX.Element => {
    const [data, setData] = useState<IAdServerTagOverview>(null);
    const theme= useTheme()
    const [specificComparisonDate, setSpecificComparisonDate] = useState()
    const [graphData, setGraphData] = useState<IOverviewGraphResponse>(null);
    const [optionalComparisonDates, setOptionalComparisonDate] = useState<any>(getComparisonOptionsDates(startDate, endDate))
    const [graphInterval, setGraphInterval] = useState<number>(getDefaultTimeDisplay(startDate, endDate))
    useRefreshInterval(()=>{
        if (startDate === endDate && startDate === moment().format("YYYY-MM-DD")) {
            fetchData()
            fetchGraphData()
        }
    })
    const [comparisonData, setComparisonData] = useState<ComparisonState>({
        compare: selectedComparison,
        data: null
    })

    useEffect(() => {
        if (specificComparisonDate && !checkIfDateIsInComparisonOptions(specificComparisonDate, optionalComparisonDates)) {
            fetchData()
            fetchGraphData()
        }
    }, [specificComparisonDate])


    const params = useParams<{ id: string }>()
    const dispatch = useDispatch();

    const fetchData = () => {
        (async () => {
            try {
                setIsLoading({isLoadingGeneral: true});
                const baseUrl = `${AdServerTagsBaseUrl}/${params.id}`;
                const apiResponse = await apiClient.post(baseUrl, {
                    startDate,
                    endDate,
                    specificComparisonDate: specificComparisonDate || null
                })
                const apiData: IAdServerTagOverview = apiResponse.data
                setData(apiData)

            } catch (err) {
                setAlert({msg: "Could not fetch data", type: AlertTypes.FAILURE, d: dispatch})
            } finally {
                setIsLoading({isLoadingGeneral: false});
            }
        })()

    };

    const fetchGraphData = () => {
        (async () => {
            try {
                setIsLoading({isLoadingGraph: true});
                const baseUrl = `${AdServerTagsBaseUrl}/${params.id}/graph`
                const graphResponse = await apiClient.post(baseUrl, {
                    startDate,
                    endDate,
                    specificComparisonDate: specificComparisonDate || null,
                    timeInterval: indexToTimeInterval(graphInterval)
                })
                const graphData = graphResponse.data
                setGraphData(graphData)
            } catch (err) {
                setAlert({msg: "Couldn't fetch Graph Data", type: AlertTypes.FAILURE, d: dispatch})
            } finally {
                setIsLoading({isLoadingGraph: false});
            }
        })()
    }

    const updateTimeInterval = () => {
        const newInterval = getDefaultTimeDisplay(startDate, endDate);
        if (newInterval !== graphInterval) {
            setGraphInterval(getDefaultTimeDisplay(startDate, endDate))
        } else {
            fetchGraphData()
        }
    }

    const updateComparisonData = ()=>{
        const dataForComparison = data
        let comparisonData;

        switch (selectedComparison) {
            case 1:
                comparisonData = dataForComparison?.dayAgo[0]?.totals
                break;
            case 7:
                comparisonData = dataForComparison?.weekAgo[0]?.totals
                break;
            case 28:
                comparisonData = dataForComparison?.monthAgo[0]?.totals
                break
            case 30:
                comparisonData = dataForComparison?.fullMonthAgo[0]?.totals
                break
            case -1:
                if(dataForComparison?.specificComparisonDate){
                comparisonData = dataForComparison?.specificComparisonDate[0]?.totals
                }
                break;
            default:
                comparisonData = dataForComparison?.selectedDates[0]?.totals
                break;
        }
        setComparisonData({compare: selectedComparison, data: comparisonData})
    }


    useEffect(fetchData, [startDate, endDate, params.id])
    useEffect(updateTimeInterval, [startDate, endDate])
    useEffect(fetchGraphData, [params.id, graphInterval]);
    useEffect(updateComparisonData, [data,selectedComparison]);


    const ReportHeaderWithProps = () => <ReportHeader breadcrumbsIcon={<Tag/>} breadcrumbs={[
        {text: "Ad Server Tags", link: `${urlService.adServerTags()}`, icon: <TocIcon />},
        {
            text: data?.tag?.dataPoint?.name || "Loading...",
            link: urlService.editDataPoint(data?.tag?.dataPoint?._id || ":id"),
            icon: <Person4Icon />
        },
        {text: data?.tag?.name || "Loading...", icon: <GpsFixedIcon />}
    ]}/>


    if (!isLoadingAny && !data) return (
        <>
            <ReportHeaderWithProps/>
            <Typography variant={"overline"}> Error with getting the data...</Typography>
        </>)
    return (
        <>
            <ReportHeaderWithProps/>
            <Grid container justifyContent={"flex-start"} alignItems={"stretch"}
                  style={{marginRight: "15px", filter: theme.palette.mode === 'dark'? 'drop-shadow(0px 0px 2px lightgray' : 'drop-shadow(0px 0px 5px lightgray'}}
                  spacing={2}>
                <>
                    {/* Row 1: Comparison (left) and Tag Info (right) */}
                    <Grid size={{ xs: 12, lg: 4 }} sx={{ flexShrink: 0, paddingLeft: { xs: 0, lg: '12px' } }}>
                        <MetricsComparison optionalComparisonDates={optionalComparisonDates} specificComparisonDate={specificComparisonDate}
                                           setSpecificComparisonDate={setSpecificComparisonDate}/>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 8 }} sx={{ flexShrink: 1, minWidth: 0, paddingRight: { xs: 0, lg: '12px' } }}>
                        {isLoading.isLoadingGeneral || !data ?
                            <Skeleton sx={{borderRadius: "16px"}} variant={"rounded"} height={"80px"} width={"100%"} animation={"wave"}/> :
                            <Paper elevation={5}
                                   style={{
                                       padding: "10px 0",
                                       height: '100%',
                                       margin: '0 0 0 10px',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       borderRadius: "16px", 
                                       boxShadow: "none"
                                   }}>
                                <TagInfo data={data} />
                            </Paper>}
                    </Grid>
                    
                    {/* Row 2: Metrics spanning full width */}
                    <Grid size={12} sx={{ paddingLeft: { xs: 0, lg: '12px' }, paddingRight: { xs: 0, lg: '12px' } }}>
                        {isLoading.isLoadingGeneral || !data ?
                            <Skeleton sx={{borderRadius: "16px"}} variant={"rounded"} height={"80px"} width={"100%"} animation={"wave"}/> :
                            <Paper elevation={5}
                                   style={{
                                       padding: "10px 0",
                                       height: '100%',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       borderRadius: "16px", 
                                       boxShadow: "none"
                                   }}>
                                <TagMetrics data={data} comparisonData={comparisonData} />
                            </Paper>}
                    </Grid>
                    <Grid item sx={{height: '400px', margin: '0 auto', width: {xs: '95%', sm: '96%', md: '99%'}}}>
                        <ComparisonOverviewGraph {...{
                            graphData,
                            startDate,
                            endDate,
                            setNewInterval: setGraphInterval,
                            graphIntervalIndx: graphInterval,
                            isLoading: isLoading.isLoadingGraph,
                            setAlert
                        }} />
                    </Grid>
                    <Grid item xs={12} style={{margin: '0 10px'}}>
                        {!isLoading.isLoadingGeneral && data ?
                            <ConnectedTagsTable items={{tags:data?.connectedTags || [], publisherTags: data?.publisherTags}}
                                                loading={isLoading.isLoadingGeneral} refreshFunc={fetchData}
                                                onConnectedTagCheckboxClick={null} side={data.tag.side}
                                                compareData={data[metricsComparisonKeyMap[comparisonData.compare]]? data[metricsComparisonKeyMap[comparisonData.compare]][0]?.connectedTags : []}
                            /> :
                            <Skeleton sx={{borderRadius: "16px"}} variant={"rounded"}animation={"wave"} height={"300px"}/>}
                    </Grid>
                </>
            </Grid>
        </>)

}


export default connector(AdServerTagPage);